/**
 * useDataGridRangeSelection
 *
 * Manages anchor/focus range state, computes normalized rectangle,
 * and exposes O(1) cell-in-range checks and perimeter position helpers.
 *
 * Plan 124, Epic 01 — Range Selection State & Visual Highlight
 */

import { useState, useMemo, useCallback } from 'react';
import type {
  CellPosition,
  CellRange,
  CellRangePosition,
  NormalizedRect,
  RangeSize,
} from './types';

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface UseDataGridRangeSelectionOptions {
  /** All visible column IDs in display order (including readonly, excluding __select) */
  visibleColumnIds: string[];
}

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export interface UseDataGridRangeSelectionReturn {
  anchor: CellPosition | null;
  focus: CellPosition | null;
  selectionRange: CellRange | null;
  normalizedRect: NormalizedRect | null;
  rangeSize: RangeSize | null;
  setRange: (anchor: CellPosition, focus: CellPosition) => void;
  clearRange: () => void;
  isCellInRange: (rowIndex: number, columnId: string) => boolean;
  getCellRangePosition: (rowIndex: number, columnId: string) => CellRangePosition | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDataGridRangeSelection(
  opts: UseDataGridRangeSelectionOptions,
): UseDataGridRangeSelectionReturn {
  const [anchor, setAnchor] = useState<CellPosition | null>(null);
  const [focus, setFocus] = useState<CellPosition | null>(null);

  // Map column ID → visual index (O(1) lookup). Excludes __select.
  const columnIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    let idx = 0;
    for (const id of opts.visibleColumnIds) {
      if (id === '__select') continue;
      map.set(id, idx++);
    }
    return map;
  }, [opts.visibleColumnIds]);

  // Normalized rectangle from anchor/focus — null when no multi-cell range
  const normalizedRect = useMemo<NormalizedRect | null>(() => {
    if (!anchor || !focus) return null;
    // Single cell (collapsed range) — no visual range
    if (anchor.rowIndex === focus.rowIndex && anchor.columnId === focus.columnId) {
      return null;
    }
    const anchorColIdx = columnIndexMap.get(anchor.columnId);
    const focusColIdx = columnIndexMap.get(focus.columnId);
    if (anchorColIdx === undefined || focusColIdx === undefined) return null;

    return {
      minRow: Math.min(anchor.rowIndex, focus.rowIndex),
      maxRow: Math.max(anchor.rowIndex, focus.rowIndex),
      minColIdx: Math.min(anchorColIdx, focusColIdx),
      maxColIdx: Math.max(anchorColIdx, focusColIdx),
    };
  }, [anchor, focus, columnIndexMap]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const setRange = useCallback((a: CellPosition, f: CellPosition) => {
    setAnchor(a);
    setFocus(f);
  }, []);

  const clearRange = useCallback(() => {
    setAnchor(null);
    setFocus(null);
  }, []);

  // ---------------------------------------------------------------------------
  // O(1) queries
  // ---------------------------------------------------------------------------

  const isCellInRange = useCallback(
    (rowIndex: number, columnId: string): boolean => {
      if (!normalizedRect) return false;
      const colIdx = columnIndexMap.get(columnId);
      if (colIdx === undefined) return false;
      return (
        rowIndex >= normalizedRect.minRow &&
        rowIndex <= normalizedRect.maxRow &&
        colIdx >= normalizedRect.minColIdx &&
        colIdx <= normalizedRect.maxColIdx
      );
    },
    [normalizedRect, columnIndexMap],
  );

  // Returns which perimeter edges this cell is on, or null if not in range.
  // Note: creates a new object per call — acceptable because the return value
  // is consumed inline for className/style computation, never stored as a dep.
  const getCellRangePosition = useCallback(
    (rowIndex: number, columnId: string): CellRangePosition | null => {
      if (!normalizedRect) return null;
      const colIdx = columnIndexMap.get(columnId);
      if (colIdx === undefined) return null;
      if (
        rowIndex < normalizedRect.minRow ||
        rowIndex > normalizedRect.maxRow ||
        colIdx < normalizedRect.minColIdx ||
        colIdx > normalizedRect.maxColIdx
      ) {
        return null;
      }
      return {
        top: rowIndex === normalizedRect.minRow,
        bottom: rowIndex === normalizedRect.maxRow,
        left: colIdx === normalizedRect.minColIdx,
        right: colIdx === normalizedRect.maxColIdx,
      };
    },
    [normalizedRect, columnIndexMap],
  );

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const rangeSize = useMemo<RangeSize | null>(() => {
    if (!normalizedRect) return null;
    const rows = normalizedRect.maxRow - normalizedRect.minRow + 1;
    const cols = normalizedRect.maxColIdx - normalizedRect.minColIdx + 1;
    return { rows, cols, total: rows * cols };
  }, [normalizedRect]);

  const selectionRange = useMemo<CellRange | null>(() => {
    if (!anchor || !focus) return null;
    return { anchor, focus };
  }, [anchor, focus]);

  return {
    anchor,
    focus,
    selectionRange,
    normalizedRect,
    rangeSize,
    setRange,
    clearRange,
    isCellInRange,
    getCellRangePosition,
  };
}
